import { useLoginState } from './useLoginState.js';
import { useDispatch } from 'react-redux';
import { setDomain } from '../state/loginSlice.js';
import { getPreference, usePreferences } from './usePreferences.js';
import { getDevice } from './useDevice.js';
import { usePlatform } from './usePlatform.js';

/**
 * Get the currently selected domain
 * @returns {string} The domain of the backend server, empty string if not set or web platform
 */
export function useBackendDomain(): [string | null, (domain: string) => Promise<string | null>] {
	const loginState = useLoginState();
	const dispatch = useDispatch();
	const platform = usePlatform();

	const [, setDomainLocal] = usePreferences('backend-domain');

	const setDomainFunc = async (domain: string) => {
		setDomainLocal(domain);
		dispatch(setDomain(domain));
		return domain;
	};

	if (!platform || !loginState.domain || platform === 'web') {
		return [null, setDomainFunc];
	}
	return [loginState.domain, setDomainFunc];
}

/**
 * Asynchronously get the domain of the backend server
 * @returns { Promise<string | null> } The domain of the backend server, null if error or web platform
 */

export async function getBackendDomain(): Promise<string | null> {
	const platform = (await getDevice())?.platform;
	const domainValue = await getPreference('backend-domain');

	if (!domainValue || !platform || platform === 'web') {
		return null;
	}

	return domainValue;
}

/**
 * Get the currently selected domain
 * @returns {string} The domain of the backend server, empty string if not set or web platform
 */
export function useImageToken(): string | null {
	const loginState = useLoginState();
	return loginState.imageToken;
}

/**
 * Convenience function to get the backend URL, for non-web platforms.
 * @param { string } url The URL to transform
 * @param { domain } domain The domain of the backend server, including protocol and port, https://example.com:3000, if null, the url is returned unchanged,
 * and this means we are on web
 * @param { string } token The token to append to the URL.
 * @param { string } tokenName The token name to use
 * @returns { string } The full URL with the backend domain and token appended, if applicable
 */
export function getBackendURL(url: string, domain?: string | null, token?: string | null, tokenName?: string): string {
	if (!domain) {
		return url;
	}

	if (url.startsWith('http')) {
		return url;
	}
	// remove trailing slash from domain, if any
	const domainPath = domain.endsWith('/') ? domain.slice(0, -1) : domain;
	let protocol = 'https://';
	if (domainPath.startsWith('http://') || domainPath.startsWith('https://')) {
		protocol = '';
	}
	let tokenString = '';
	if (token && tokenName) {
		tokenString = `?${tokenName}=${token}`;
	}
	return `${protocol}${domainPath}${url.startsWith('/') ? '' : '/'}${url}${tokenString}`;
}

/**
 * Hook to get the backend URL for images without having to first get the domain or image token.
 * @param { string } url The URL to transform
 * @returns { string } The full URL with the backend domain and image token appended.
 */
export function useBackendImageURL(url: string): string {
	const [domain] = useBackendDomain();
	const token = useImageToken();
	if (!domain || !token || !url || url.startsWith('/assets') || url.startsWith('http')) {
		return url;
	}
	return getBackendURL(url, domain, token, '_jwt_image_token');
}

/**
 * Hook to get the backend URLs for images without having to first get the domain or image token.
 * @param { string[] } urls The URL array to transform
 * @returns { string[] } The array full URL with the backend domain and image token appended.
 */
export function useBackendImageURLs(urls: string[]): string[] {
	const [domain] = useBackendDomain();
	const token = useImageToken();
	if (!domain || !token) {
		return urls;
	}
	return urls.map(url =>
		getBackendURL(
			url,
			url.startsWith('/assets') || url.startsWith('http') ? null : domain,
			token,
			'_jwt_image_token'
		)
	);
}
